import { Injectable } from "@angular/core";
import { map, Subject, take, tap } from "rxjs";
import { BeatYourBrokerResultModel } from "src/app/models/beat-your-broker-result.model";
import { QQQuestionnairePageViewModel } from "src/app/models/qq-questionnaire-page.view-model";
import { QQQuestionnaireViewModel } from "src/app/models/qq-questionnaire.view-model";
import { QuickQuoteRequest } from "src/app/models/quick-quote-request";
import { QuoteModel } from "src/app/models/quote.model";
import { ApiService } from "src/app/services/api.service";
import { SharedStateService } from "src/app/services/shared-state.service";
import { StorageService } from "src/app/services/storage.service";

@Injectable()
export class BeatYourBrokerHelper {

    allowedPages: number[] = [];

    get result$() {
        return this._result$.asObservable();
    }

    private _result$ = new Subject<{ quote: QuoteModel, mode: string, cssClass: string }>();

    constructor(
        private api: ApiService,
        private storageService: StorageService,
        private sharedStateService: SharedStateService<QQQuestionnaireViewModel>
    ) {
        this.allowedPages = this.storageService.retrieve('coverchecker:allowedpages') || [];
    }

    async getQuote(quickQuote: QQQuestionnaireViewModel) {
        const answers = [...quickQuote.answers.entries()]
            .map(x => ({ Key: x[0], Value: x[1] }));

        const request = new QuickQuoteRequest(quickQuote.quoteKey, answers, false);
        const rsp = await this.api.submitQuickQuote(quickQuote.questionnaire.name, request);
        if (rsp.error) {
            return { error: rsp.error };
        }

        const newQuote = new QuoteModel(quickQuote.questionnaire.name, {
            key: rsp.key,
            answers: [...answers].map(x => ({ key: x.Key, value: x.Value })),
            result: rsp
        });

        return { quote: newQuote };
    }

    calculateResult(quote: QuoteModel) {
        const answers = quote.answers.toDictionary(x => x.key, x => x.value);
        const currentPremium = parseInt(answers['CurrentPremium']);
        const provider = answers['CurrentProvider'];

        let rsp;

        if (!provider || provider === "None" || !currentPremium || currentPremium <= 0) {
            rsp = this.getResult(answers['TypeOfPolicy_1'], answers['PolicyType_1'], 0, 0, false);
            return rsp;
        }

        rsp = this.getResult(answers['TypeOfPolicy_1'], answers['PolicyType_1'], quote.result.premium, currentPremium, true);
        return rsp;
    }

    getResult(typeOfPolicy: string, policyType: string, premium: number, currentPremium: number, hasCover: boolean) {
        const canBetter = (typeOfPolicy === 'DUAL' || policyType === 'TERM-CI')
            ? !hasCover || currentPremium > premium
            : !hasCover || (currentPremium + (currentPremium * 0.10)) > premium;
        //Assura price is greater than >Existing pice over up to + 10%

        const displayQuote = !hasCover || ((typeOfPolicy === 'DUAL' || policyType === 'TERM-CI') && canBetter);

        let result = new BeatYourBrokerResultModel({ displayQuote, canBetter, hasCover, currentPremium });
        result.assuraPremium = premium;
        if (canBetter) {
            result.savings = currentPremium - premium;
        }

        result.contentKey1 = `BeatYourBroker.${policyType === 'TERM-CI' ? 'CI_1'
            : typeOfPolicy === 'DUAL' ? 'DUAL_1'
                : hasCover ? `SINGLE_${canBetter ? 'TRUE' : 'FALSE'}` : 'SINGLE_NoCover'}`;

        if (typeOfPolicy === 'DUAL' || policyType === 'TERM-CI') {
            result.contentKey2 = `BeatYourBroker.${policyType === 'TERM-CI' ? 'CI_2' : 'DUAL_2'}`;
            if (!hasCover)
                result.contentKey2 += '_NoCover';
        }

        result.showContactRequest = (typeOfPolicy === 'DUAL' || policyType === 'TERM-CI' || canBetter);

        return result;
    }

    async saveContactDate(key: string, date: Date) {
        await this.api.saveContactRequestDate(key, {
            preferredContactDate: date
        });
    }

    getNextPage(questionnaire: QQQuestionnaireViewModel, page: QQQuestionnairePageViewModel) {
        return questionnaire.visiblePages$.pipe(
            tap(() => this.sharedStateService.updateState(questionnaire)),
            map(pages => {
                const idx = pages.indexOf(page);
                if (idx >= pages.length - 1)
                    return page.index;
                return pages[idx + 1].index;
            })
        );
    }

    getPrevPage(questionnaire: QQQuestionnaireViewModel, page: QQQuestionnairePageViewModel, count: number = 1) {
        return questionnaire.visiblePages$.pipe(
            tap(() => this.sharedStateService.updateState(questionnaire)),
            map(pages => {
                const idx = pages.indexOf(page);
                if (idx <= count)
                    return 0;
                return pages[idx - count].index;
            }),
            take(1)
        );
    }

    allowPage(index: number) {
        if (!this.allowedPages.includes(index)) {
            this.allowedPages.push(index);
            this.storageService.save('coverchecker:allowedpages', this.allowedPages);
        }
    }

    triggerResult(quote: QuoteModel, mode: string, cssClass: string) {
        this._result$.next({ quote, mode, cssClass });
    }

}
